import { useContactProfileQuery } from '@/hooks/queries/use-contact-profile-query';
import { useTranslation } from 'react-i18next';
import { type ContactPlatform } from '../contact-drawer-url-state';
import type { ContactShell, ContactProfile } from '../contact-types';
import { platformEnumOf } from '../platform-of';
import {
  useIsContactResolving,
  useResolvedContactIdentityId,
} from '../use-resolved-contact-identity-id';
import { mapCreatorProfileToContact } from './map-creator-profile-to-contact';

/**
 * Resolves the active `ContactProfile` for the PROFILE view, with whether it is
 * still loading.
 *
 * Reads the real profile slice of the selected platform (lazy, keyed by the
 * resolved `creatorIdentityId` and the platform) and maps it into the
 * per-platform shape the view's sub-components consume. Returns `undefined`
 * when the identity has no live profile (loading, error, or 404, the dominant
 * early path for un-enriched identities); the view renders an explicit empty
 * state rather than fabricating one.
 *
 * The query follows the platform toggle, so the backend answers for the selected
 * platform. The live slice is still used only when its platform matches the
 * selected one: a backend answering for another platform must not relabel its
 * stats. Without a matching live slice it falls back to that platform's summary
 * rail row (handle + followers) marked no-live-metrics, and the view renders the
 * measured stats as an explicit no-data state. When the selected platform has no
 * rail row either (a deep-link to a platform the contact does not have), it
 * returns `undefined` so the view shows its empty state.
 */
export const useActiveContactProfileState = (
  contact: ContactShell,
  platform: ContactPlatform
): { profile: ContactProfile | undefined; isLoading: boolean } => {
  const { t, i18n } = useTranslation();
  const resolving = useIsContactResolving();
  const creatorIdentityId = useResolvedContactIdentityId();
  const { data, isLoading } = useContactProfileQuery(creatorIdentityId, platformEnumOf(platform));
  // The first resolve, then the first fetch of the slice: the view shows its
  // skeleton rather than the summary fallback below, which would flash no-data
  // notices for metrics that are simply not there yet.
  const loading = resolving || isLoading;

  const live = data ? mapCreatorProfileToContact(data, t, i18n.language) : undefined;
  if (live && live.platform === platform) {
    return { profile: live, isLoading: loading };
  }

  // No live profile for the selected platform: fall back to that platform's rail
  // row (summary-only). It has no measured stats/audience, so mark it
  // no-live-metrics — the view renders an explicit no-data state rather than
  // fabricating zeros (or presenting another platform's live stats). When the
  // selected platform has no rail row at all (e.g. a deep-link to a platform the
  // contact does not have), return undefined so the view shows its empty state
  // instead of silently relabelling another platform's profile.
  const fallback = contact.profiles.find((p) => p.platform === platform);
  return {
    profile: fallback ? { ...fallback, hasLiveMetrics: false } : undefined,
    isLoading: loading,
  };
};

/** The profile alone, for callers that have no loading state to draw. */
export const useActiveContactProfile = (
  contact: ContactShell,
  platform: ContactPlatform
): ContactProfile | undefined => useActiveContactProfileState(contact, platform).profile;
